import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Wand2 } from 'lucide-react';
import { normalizeConfig, getCategory } from '../configurator/catalog';
import { computeLayout } from '../configurator/layout';
import { makeCtx } from '../configurator/parts';
import { ViewCanvas, VIEWS } from '../configurator/ViewCanvas';
import { summaryLines, materialLines } from '../configurator/exportPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharePage() {
  const { id } = useParams();
  const [share, setShare] = useState(undefined);
  const [mode, setMode] = useState('perspective');
  const [wall, setWall] = useState('A');
  useEffect(() => {
    fetch(`${API_URL}/api/share/${id}`).then((r) => (r.ok ? r.json() : null)).then(setShare).catch(() => setShare(null));
  }, [id]);
  const cfg = useMemo(() => (share ? normalizeConfig(share.config) : null), [share]);
  const L = useMemo(() => (cfg ? computeLayout(cfg) : null), [cfg]);
  const c = useMemo(() => (cfg ? makeCtx('sh', cfg.mats, cfg.led, false, cfg.facade) : null), [cfg]);

  if (share === undefined) return <div className="min-h-screen bg-[#121212] flex items-center justify-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>;
  if (!share) return (
    <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="share-not-found">
      <h1 className="font-playfair text-3xl text-white">Ce lien n'existe plus</h1>
      <a href="/configurateur" className="bg-gold text-dark font-semibold px-6 py-3">Créer mon projet</a>
    </div>
  );
  const cat = getCategory(share.category);
  return (
    <div className="min-h-screen bg-[#121212] text-gray-200 pt-28 lg:pt-44 pb-24" data-testid="share-page">
      <div className="container mx-auto px-6">
        <p className="text-[11px] uppercase tracking-[0.4em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-px bg-gold" />Projet partagé</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <h1 className="font-playfair text-4xl sm:text-5xl text-white">{share.name || `Projet ${cat.label}`}</h1>
          <a href={`/configurateur?cat=${share.category}&share=${share.id}`} data-testid="share-create-my-version"
            className="group flex items-center gap-2 bg-gold hover:bg-gold-light text-dark font-semibold px-6 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
            <Wand2 className="w-4 h-4 transition-transform group-hover:rotate-12" />Créer ma version
          </a>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(VIEWS).map(([k, v]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`share-view-${k}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] ${mode === k ? 'bg-[#a68966] text-[#1a1a1a]' : 'text-gray-400 border border-white/10'}`}>{v.label}</button>
          ))}
          {mode === 'elevation' && L.walls.length > 1 && L.walls.map((w) => (
            <button key={w} type="button" onClick={() => setWall(w)} data-testid={`share-wall-${w}`} className={`px-3 py-2 text-xs ${wall === w ? 'text-[#4dffb8] border border-[#4dffb8]/50' : 'text-gray-500 border border-white/10'}`}>Mur {w}</button>
          ))}
        </div>
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="bg-[#f6f1e8] h-[55vh] lg:h-[68vh]" key={`${mode}${wall}`}><ViewCanvas kind={mode} L={L} c={c} sel={null} onPick={() => {}} p={`sh${mode}`} wall={wall} /></div>
          <aside className="border border-white/10 p-5 text-xs text-gray-400 space-y-5">
            <div><p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Composition</p><ul className="space-y-1">{summaryLines(cfg, L).map((l) => <li key={l}>{l}</li>)}</ul></div>
            <div><p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Matériaux</p><ul className="space-y-1">{materialLines(cfg).map((l) => <li key={l}>{l}</li>)}</ul></div>
          </aside>
        </div>
      </div>
    </div>
  );
}
